'use client';

/**
 * Spec 80 §7.2 / §10 — the Vercel "Copy as Markdown" / "Copy section"
 * affordances. Two buttons:
 *
 *   - `<CopyPageMarkdownButton>` — fetches the page's raw markdown from
 *     the spec 31 `.md` content-negotiation endpoint
 *     (`/docs/<slug>/index.md`) and copies the whole thing.
 *   - `<CopySectionButton>` — copies a single H2 section's markdown,
 *     already sliced server-side by `lib/doc-headings.ts` and passed in
 *     as a plain string prop (no client-side parsing needed).
 *
 * Mirrors `SecretField`'s copy feedback contract: the icon swaps to a
 * green check for a couple seconds then fades back to idle, and a
 * visually-hidden `aria-live="polite"` region announces success/failure
 * for screen readers. FontAwesome icons only (repo icon-library rule).
 */
import { faCheck, faCopy, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/ui/components/ui/button';
import { useCallback, useId, useRef, useState } from 'react';
import { AsyncButton } from '@/components/async-button';

type CopyState = 'idle' | 'copied' | 'error';

const FEEDBACK_MS = 1800;

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the execCommand fallback (non-secure context / denied)
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Shared copy-state + feedback-timer + aria-live announcement plumbing. */
function useCopyFeedback(label: string) {
  const [state, setState] = useState<CopyState>('idle');
  const [announce, setAnnounce] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveId = useId();

  const settle = useCallback(
    (ok: boolean) => {
      setState(ok ? 'copied' : 'error');
      setAnnounce(ok ? `${label} copied to clipboard` : `Failed to copy ${label}`);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setState('idle');
        setAnnounce('');
      }, FEEDBACK_MS);
    },
    [label],
  );

  const icon = state === 'copied' ? faCheck : state === 'error' ? faTriangleExclamation : faCopy;
  const iconTint = state === 'copied' ? 'text-success-500' : state === 'error' ? 'text-warning-500' : undefined;

  const liveRegion = (
    <span id={liveId} role="status" aria-live="polite" className="sr-only">
      {announce}
    </span>
  );

  return { state, settle, icon, iconTint, liveRegion };
}

export function CopyPageMarkdownButton({ slug }: { slug: string }) {
  const { state, settle, icon, iconTint, liveRegion } = useCopyFeedback('Page markdown');

  const handleClick = useCallback(async () => {
    try {
      const res = await fetch(`/docs/${slug}/index.md`);
      if (!res.ok) throw new Error(`unexpected status ${res.status}`);
      const markdown = await res.text();
      settle(await writeClipboard(markdown));
    } catch {
      settle(false);
    }
  }, [slug, settle]);

  return (
    <>
      <AsyncButton
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        pendingLabel="Copying…"
        className="gap-2"
      >
        <FontAwesomeIcon icon={icon} aria-hidden className={iconTint} />
        {state === 'copied' ? 'Copied!' : 'Copy as Markdown'}
      </AsyncButton>
      {liveRegion}
    </>
  );
}

export function CopySectionButton({ heading, markdown }: { heading: string; markdown: string }) {
  const { settle, icon, iconTint, liveRegion } = useCopyFeedback(`"${heading}" section`);

  const handleClick = useCallback(async () => {
    settle(await writeClipboard(markdown));
  }, [markdown, settle]);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={handleClick}
        aria-label={`Copy "${heading}" section as markdown`}
        title="Copy section"
        className="shrink-0 text-muted-foreground"
      >
        <FontAwesomeIcon
          icon={icon}
          aria-hidden
          className={`h-3.5 w-3.5 transition-colors duration-200 ${iconTint ?? ''}`}
        />
      </Button>
      {liveRegion}
    </>
  );
}
